import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, Mail, MapPin, Clock, ChevronUp, Facebook, Instagram } from "lucide-react";
import { Button } from "./ui/button";

const navLinks = [
  { path: "/", label: "Főoldal" },
  { path: "/szolgaltatasok", label: "Szolgáltatások" },
  { path: "/galeria", label: "Galéria" },
  { path: "/arak", label: "Árak" },
  { path: "/idopontfoglalas", label: "Időpontfoglalás" },
  { path: "/velemenyek", label: "Vélemények" },
  { path: "/rolunk", label: "Rólunk" },
  { path: "/kapcsolat", label: "Kapcsolat" },
];

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 500);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="hidden lg:block bg-[#0A0A0A] border-b border-[#2A2A2A] py-2">
        <div className="container mx-auto px-6 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6 text-[#A3A3A3]">
            <a href="tel:+36201234567" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Phone size={14} />
              <span>+36 20 123 4567</span>
            </a>
            <a href="mailto:demo@referencia-auto.demo" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Mail size={14} />
              <span>demo@referencia-auto.demo</span>
            </a>
          </div>
          <div className="flex items-center gap-6 text-[#A3A3A3]">
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>H-P: 8:00-18:00 | Sz: 9:00-14:00</span>
            </div>
            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-[#D4AF37] transition-colors" aria-label="Facebook">
                <Facebook size={16} />
              </a>
              <a href="#" className="hover:text-[#D4AF37] transition-colors" aria-label="Instagram">
                <Instagram size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-[#050505]/95 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
                <span className="font-heading font-bold text-black text-xl">P</span>
              </div>
              <div className="hidden sm:block">
                <span className="font-heading text-lg text-[#E5E5E5] tracking-wide">DEMO AUTO</span>
                <span className="block text-xs text-[#A3A3A3] tracking-widest">KOZMETIKA (REFERENCIA)</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.replace("/", "") || "home"}`}
                  className={`nav-link px-4 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
                    location.pathname === link.path
                      ? "text-[#D4AF37]"
                      : "text-[#A3A3A3] hover:text-[#E5E5E5]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <Link to="/idopontfoglalas">
                <Button
                  data-testid="header-booking-btn"
                  className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-6 py-2 rounded-sm"
                >
                  Foglalás
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-[#E5E5E5]"
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-[#0A0A0A] border-t border-[#2A2A2A]"
            >
              <nav className="container mx-auto px-6 py-4 flex flex-col">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                      className={`block py-3 text-base font-medium tracking-wide border-b border-[#1E1E1E] ${
                        location.pathname === link.path
                          ? "text-[#D4AF37]"
                          : "text-[#A3A3A3]"
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <Link to="/idopontfoglalas" className="mt-4">
                  <Button
                    data-testid="mobile-booking-btn"
                    className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] py-4 rounded-sm"
                  >
                    Időpont Foglalás
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-grow">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-[#2A2A2A]">
        <div className="container mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37] rounded-sm flex items-center justify-center">
                  <span className="font-heading font-bold text-black text-xl">D</span>
                </div>
                <div>
                  <span className="font-heading text-lg text-[#E5E5E5] tracking-wide">DEMO AUTO</span>
                  <span className="block text-xs text-[#A3A3A3] tracking-widest">KOZMETIKA (REFERENCIA)</span>
                </div>
              </div>
              <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6">
                Ez egy referencia bemutató oldal, amely demonstrálja az autókozmetikai 
                iparág számára készült professzionális webes megoldásokat.
              </p>
              <div className="flex gap-4">
                <a
                  href="#"
                  className="w-10 h-10 bg-[#1E1E1E] border border-[#2A2A2A] rounded-sm flex items-center justify-center text-[#A3A3A3] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
                  aria-label="Facebook"
                >
                  <Facebook size={18} />
                </a>
                <a
                  href="#"
                  className="w-10 h-10 bg-[#1E1E1E] border border-[#2A2A2A] rounded-sm flex items-center justify-center text-[#A3A3A3] hover:text-[#D4AF37] hover:border-[#D4AF37] transition-all"
                  aria-label="Instagram"
                >
                  <Instagram size={18} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-heading text-lg text-[#E5E5E5] mb-6 tracking-wide">NAVIGÁCIÓ</h4>
              <ul className="space-y-3">
                {navLinks.slice(0, 5).map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading text-lg text-[#E5E5E5] mb-6 tracking-wide">SZOLGÁLTATÁSOK</h4>
              <ul className="space-y-3 text-sm text-[#A3A3A3]">
                <li>Külső–Belső Detailing</li>
                <li>Kerámia Bevonat</li>
                <li>Polírozás</li>
                <li>Bőrápolás</li>
                <li>Ózonos Fertőtlenítés</li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading text-lg text-[#E5E5E5] mb-6 tracking-wide">KAPCSOLAT</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3 text-sm">
                  <MapPin size={18} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-[#A3A3A3]">1000 Budapest, Demo utca 1. (Referencia cím)</span>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Phone size={18} className="text-[#D4AF37] flex-shrink-0" />
                  <a href="tel:+36201234567" className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
                    +36 20 123 4567
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Mail size={18} className="text-[#D4AF37] flex-shrink-0" />
                  <a href="mailto:demo@referencia-auto.demo" className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors">
                    demo@referencia-auto.demo
                  </a>
                </li>
                <li className="flex items-center gap-3 text-sm">
                  <Clock size={18} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-[#A3A3A3]">H-P: 8:00-18:00 | Sz: 9:00-14:00</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-[#1E1E1E] flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#666] text-sm">
              © 2024 Demo Auto Kozmetika - Referencia oldal. Fejlesztés: <a href="https://barasuite.hu" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:text-[#B5952F] transition-colors">BaraSuite</a>
            </p>
            <div className="flex gap-6 text-sm text-[#666]">
              <a href="#" className="hover:text-[#A3A3A3] transition-colors">Adatvédelem</a>
              <a href="#" className="hover:text-[#A3A3A3] transition-colors">ÁSZF</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 w-12 h-12 bg-[#D4AF37] text-black rounded-sm flex items-center justify-center shadow-lg hover:bg-[#B5952F] transition-colors z-40"
            data-testid="scroll-top-btn"
            aria-label="Scroll to top"
          >
            <ChevronUp size={24} />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Layout;
